import {
  CreateStandaloneFile,
  DownloadOptions,
  File as FileT,
  FileId,
  UploadOptions,
  CreateFileUploadSession,
  FileUploadSessionId,
} from "platformed-client/types";
import {platformedClient} from ".";
import {HTTPError} from "platformed-client";

export class NetworkError extends Error {}

export async function uploadImpl(
  url: string,
  file: File,
  progressFn?: (progress: number) => void,
  signal?: AbortSignal,
): Promise<FileT> {
  // Make sure the signal isn't already aborted
  signal?.throwIfAborted();
  const xhr = new XMLHttpRequest();
  const abort = () => {
    xhr.abort();
  };
  signal?.addEventListener("abort", abort);

  const formData = new FormData();
  formData.append("file", file);

  if (progressFn) {
    xhr.upload.onprogress = ev => {
      progressFn(ev.loaded / ev.total);
    };
  }

  try {
    return await new Promise((resolve, reject) => {
      xhr.onreadystatechange = () => {
        switch (xhr.readyState) {
          // Cancelled
          case 0:
            reject(signal?.reason);
            break;
          // Completed
          case 4:
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(xhr.response);
            } else if (xhr.status !== 0) {
              reject(
                new HTTPError(
                  new Response(JSON.stringify(xhr.response), {status: xhr.status, statusText: xhr.statusText}),
                  undefined,
                ),
              );
            } else {
              reject(new NetworkError("Unknown network error"));
            }
            break;
          default:
            break;
        }
      };

      xhr.responseType = "json";
      xhr.withCredentials = true;
      xhr.open("POST", url, true);
      xhr.send(formData);
    });
  } finally {
    signal?.removeEventListener("abort", abort);
  }
}

export async function upload(
  file: File,
  options: UploadOptions = {system_wide: false},
  progressFn?: (progress: number) => void,
  signal?: AbortSignal,
): Promise<FileT> {
  const url = platformedClient.internalUrl("post", "/region/{region}/api/files", {query: options});
  return uploadImpl(url, file, progressFn, signal);
}

export const getDownloadUrl = (fileId: FileId, options: DownloadOptions = {}) => {
  return platformedClient.internalUrl("get", "/region/{region}/api/files/{file_id}/download", {
    query: options,
    path: {file_id: fileId},
  });
};

export const download = async (fileId: FileId, options: DownloadOptions = {}) => {
  const a = document.createElement("a");
  a.href = getDownloadUrl(fileId, options);
  a.target = "_blank";
  a.click();
};

export const createStandalone = async (standaloneFile: CreateStandaloneFile) => {
  return await platformedClient.internalRequest("post", "/region/{region}/api/standalone_files", {
    path: {},
    body: standaloneFile,
  });
};

export const createUploadSession = async (create: CreateFileUploadSession) => {
  return await platformedClient.internalRequest("post", `/region/{region}/api/files/upload_session/create`, {
    body: create,
  });
};

export const completeUploadSession = async (id: FileUploadSessionId): Promise<FileT> => {
  return await platformedClient.internalRequest("post", `/region/{region}/api/files/upload_session/complete`, {
    body: id,
  });
};
